import { useState, useEffect, useCallback } from 'react';
import { IAppointment, IService, getService, useTransportationOptions, LocalizedTransportationOption } from '../../../API/GoMoto';
import { TransportSubtype, TransportType } from '../../../API/Enums';
import { useAppointmentState, useAppointmentUpdateAndPersist, useIsFullMobileCheckInAllowed } from '../../../context/Appointment/appointment';
import { ITransport, Types, useCart, useCartDispatch } from '../../../context/cart';
import { useKioskTimeTable, useKioskState } from '../../../context/Kiosk/kiosk';
import { Spinner } from '../../../controls/Spinner/Spinner';
import { getUpdateAptServices } from '../../../helpers/Appointment';
import { isElectron } from '../../../helpers/EnvironmentFlags/EnvironmentFlags';
import { GoMotoComponent, GoMotoFunction } from '../../../API/GoMoto.types';
import { TransportationProps } from './Transportation.types';
import { TransportationClosed } from './TransportationClosed/TransportationClosed';
import { TransportationOpen } from './TransportationOpen/TransportationOpen';

/**
 * This is effectively a routing component that routes the user to the Open or Closed
 * version of the component based on whether the customer using the kiosk is within the
 * Hybrid Time Table hours.
 *
 * // TODO: If this component is effectively a routing component, it may make more sense
 * to make this an actual routing component. This could reduce the total amount of "On Skip" logic that
 * causes so many problems in kiosk.
 *
 * @param onProceed - {@link TransportationProps.onProceed onProceed} description.
 * @param onSkip - {@link TransportationProps.onSkip onSkip} description.
 */
export const Transportation: GoMotoComponent<TransportationProps> = ({
    onProceed,
    onSkip,
}) => {
    const {
        key: kioskKey,
        dealer: { key: dealerKey, vehiclePickupEnabled },
    } = useKioskState();
    const { getIsKioskActuallyClosed } = useKioskTimeTable();
    const { services } = useCart();
    const { loading: transportationLoading, transportationOptions: transportOpts } = useTransportationOptions(kioskKey, dealerKey);
    const cartDispatch = useCartDispatch();
    const appointment: IAppointment = useAppointmentState();
    const setAppointment = useAppointmentUpdateAndPersist();
    const isFullMobileCheckInAllowed: boolean = useIsFullMobileCheckInAllowed();

    const [loading, setLoading] = useState<boolean>(false);
    const [shouldSkip, setShouldSkip] = useState<boolean>(false);
    const isKiosk: boolean = isElectron();
    const pickUpTimeEnabled: boolean = vehiclePickupEnabled;


    const save: GoMotoFunction<[ITransport], Promise<void>> = useCallback(async (transportation) => {
        const servicesConfirmed: IAppointment['servicesConfirmed'] = getUpdateAptServices(services);

        if (transportation.transportService) {
            servicesConfirmed.push({
                id: transportation.transportService.id,
                isTransport: true,
            });
        }

        setLoading(true);
        await setAppointment({
            ...appointment,
            servicesConfirmed,
            pickupTimeEntry: transportation.pickUpTime ?? '',
            transportation: {
                opCode: transportation.transportService?.opCode || transportation.opCode || '',
                type: transportation.type,
                subtype: transportation.subtype || TransportSubtype.none,
                dmsTransportationType: transportation.dmsTransportationType || '',
            },
        });
        cartDispatch({
            type: Types.SetTransportation,
            payload: transportation,
        });
        setLoading(false);
    }, [services, appointment, setAppointment, cartDispatch]);

    const handleOnProceed: GoMotoFunction<[ITransport], Promise<void>> = async (transportation) => {
        await save(transportation);
        onProceed();
    };

    const handleOnSkip: GoMotoFunction<[ITransport], Promise<void>> = useCallback(async (transportation) => {
        setShouldSkip(true);
        await save(transportation);
        onSkip();
    }, [save, onSkip]);

    // Auto-skip transportation screen when 0 or 1 options are available (Pt4 §1.5.1)
    useEffect(() => {
        let isMounted = true;

        if (transportationLoading || !transportOpts || shouldSkip)
            return;

        if (transportOpts.length === 0) {
            // No options — skip with no transport selection
            handleOnSkip({
                type: TransportType.drop,
                subtype: TransportSubtype.none,
                pickUpTime: undefined,
            });
        }
        else if (transportOpts.length === 1 && !transportOpts[0].showPickupTime) {
            // Exactly one option with no pickup time — auto-select it and skip
            const only: LocalizedTransportationOption = transportOpts[0];
            const fetchAndSkip = async () => {
                setLoading(true);
                const service: IService | undefined = only.opCode
                    ? await getService(kioskKey, only.opCode)
                    : undefined;
                if (isMounted) {
                    setLoading(false);
                    handleOnSkip({
                        type: only.type,
                        subtype: only.subtype,
                        transportService: service,
                        pickUpTime: undefined,
                        dmsTransportationType: only.dmsTransportationType,
                        opCode: only.opCode,
                    });
                }
            };
            fetchAndSkip();
        }
        /*
         * 1 option with showPickupTime=true: fall through to normal render
         * so the user can enter a pickup time before proceeding
         */

        return () => {
            isMounted = false;
        };
    }, [transportationLoading, transportOpts, shouldSkip, kioskKey, handleOnSkip]);

    if (!shouldSkip && !loading && transportOpts && !transportationLoading) {
        if (getIsKioskActuallyClosed() && (isKiosk || isFullMobileCheckInAllowed)) {
            return (
                <TransportationClosed
                    onProceed={handleOnProceed}
                    onSkip={handleOnSkip}
                    transportOpts={transportOpts}
                    pickUpTimeEnabled={pickUpTimeEnabled}
                />
            );
        }
        else {
            return (
                <TransportationOpen
                    onProceed={handleOnProceed}
                    transportOpts={transportOpts}
                    pickUpTimeEnabled={pickUpTimeEnabled}
                />
            );
        }
    }
    else {
        return (
            <Spinner />
        );
    }
};
